/**
 * iter87.467 (user-requested) — Playoff Predictor panel for the
 * single-elimination Playoff Predictor templates (NFL/CFB Playoffs +
 * MLS Conf Semis+). Replaces the old RoundSelect-dropdown pattern
 * (which only let you browse the current + future rounds, with past
 * rounds reachable only through a read-only points ledger) with the
 * same MLB Series Predictor shape every other multi-round predictor
 * in the app now uses: an always-visible "Open — Make Your Pick"
 * section, a "Locked & Live" section (collapsible, starts open), and
 * a "Completed" section (collapsible, starts closed, cards tinted
 * green/red for hit/miss) — every round, past and future, visible at
 * once, grouped by round within the Locked/Completed buckets.
 *
 * Sibling of `MlbSeriesPickModal.jsx` / `UclAdvancePickModal.jsx` —
 * same section shape, adapted for single-game spread + over/under
 * picks instead of series picks. Data comes from the dedicated
 * `/matchday-boost/tournaments/{id}/playoff-bracket` endpoint (ALL
 * rounds in one response), NOT the season-long
 * `matchday_boost_state` endpoint `MatchdayBoostPanel.jsx` still uses
 * for Weekly/Gameday Boost (untouched by this change).
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Info, Lock, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { SubPickLabel } from "@/components/dashboard/SubPickLabel";

function kickoffDate(iso) {
    if (!iso) return "TBD";
    try {
        return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "numeric", day: "numeric" });
    } catch { return ""; }
}
function kickoffTime(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
}
const fmtSpread = (v) => (v == null ? null : v > 0 ? `+${v}` : `${v}`);
// iter87.564.1 (testing-agent caught) — a 0-value payout (e.g. an
// admin-configured "miss = 0") previously rendered as the confusing
// "−0"; handles the sign itself instead of always assuming negative.
const fmtPts = (v) => (v > 0 ? `+${v}` : v < 0 ? `−${Math.abs(v)}` : "0");

export default function PlayoffPredictorPanel({ tournament, me, onRefresh, footerSlot }) {
    const [bracket, setBracket] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [draft, setDraft] = useState(null); // null = derive from saved
    const [saving, setSaving] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [showLive, setShowLive] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);

    const tournamentId = tournament?.id;
    const participantId = me?.id;

    const load = () => api.getPlayoffPredictorBracket(tournamentId, participantId)
        .then((data) => { setBracket(data); setLoadError(false); })
        .catch(() => setLoadError(true));

    useEffect(() => {
        if (!tournamentId) return;
        load();
        const id = setInterval(load, 45000);
        return () => clearInterval(id);
    }, [tournamentId, participantId]);

    if (!bracket) {
        return loadError ? (
            <div className="text-center py-6" data-testid="playoff-predictor-load-error">
                <p className="text-sm text-zinc-500 mb-3">Couldn't load Playoff Predictor — try again.</p>
                <button type="button" onClick={load} className="brutal-btn-ghost px-4 py-2 text-xs font-bold uppercase tracking-widest" data-testid="playoff-predictor-retry-btn">
                    Retry
                </button>
            </div>
        ) : <p className="text-sm text-zinc-500 py-6 text-center">Loading Playoff Predictor…</p>;
    }

    const isMlsPlayoffs = tournament?.template === "mls_playoffs_api";
    const spreadMode = bracket.scoring_mode === "spread";
    const payout = bracket.payout || {};
    const ouPayout = bracket.ou_payout || {};

    const allFixtures = (bracket.rounds || []).flatMap((r) =>
        (r.fixtures || []).map((f) => ({ ...f, _round: r.round, _roundLabel: r.label, _roundCap: r.picks_per_round })));
    const fixturesById = Object.fromEntries(allFixtures.map((f) => [f.fixture_id, f]));
    const notSetYet = allFixtures.length === 0;

    const openFixtures = allFixtures.filter((f) => f.status === "open");
    const liveFixtures = allFixtures.filter((f) => f.status === "locked");
    const completedFixtures = allFixtures.filter((f) => f.status === "final");
    const byRound = (list) => list.reduce((acc, f) => {
        if (!acc[f._round]) acc[f._round] = { label: f._roundLabel, fixtures: [] };
        acc[f._round].fixtures.push(f);
        return acc;
    }, {});
    const liveByRound = byRound(liveFixtures);
    const completedByRound = byRound(completedFixtures);

    const savedPicks = allFixtures.filter((f) => f.my_pick?.team_id)
        .map((f) => ({ fixture_id: f.fixture_id, team_id: f.my_pick.team_id, ou_pick: f.my_pick.ou_pick || null }));
    const effective = draft ?? savedPicks;
    const byFixture = Object.fromEntries(effective.map((p) => [p.fixture_id, p.team_id]));
    const ouByFixture = Object.fromEntries(effective.map((p) => [p.fixture_id, p.ou_pick || null]));
    const openIds = new Set(openFixtures.map((f) => f.fixture_id));

    const subsetFor = (list, r) => list
        .filter((p) => fixturesById[p.fixture_id]?._round === r)
        .sort((a, b) => a.fixture_id.localeCompare(b.fixture_id));
    const roundsInvolved = [...new Set([...effective, ...savedPicks]
        .map((p) => fixturesById[p.fixture_id]?._round).filter((r) => r != null))];
    const dirtyRounds = roundsInvolved.filter((r) =>
        JSON.stringify(subsetFor(effective, r)) !== JSON.stringify(subsetFor(savedPicks, r)));
    const dirty = dirtyRounds.length > 0;

    const toggle = (fixture, teamId) => {
        if (!me) return;
        if (fixture.status !== "open") {
            toast.error("That game already kicked off");
            return;
        }
        const cur = byFixture[fixture.fixture_id];
        let next;
        if (cur === teamId) {
            next = effective.filter((p) => p.fixture_id !== fixture.fixture_id);
        } else if (cur) {
            next = effective.map((p) => p.fixture_id === fixture.fixture_id
                ? { fixture_id: fixture.fixture_id, team_id: teamId, ou_pick: p.ou_pick || null } : p);
        } else {
            const roundCount = effective.filter((p) => fixturesById[p.fixture_id]?._round === fixture._round).length;
            if (roundCount >= fixture._roundCap) {
                toast.error(`Max ${fixture._roundCap} picks for ${fixture._roundLabel} — deselect one first`);
                return;
            }
            next = [...effective, { fixture_id: fixture.fixture_id, team_id: teamId, ou_pick: null }];
        }
        setDraft(next);
    };

    const toggleOu = (fixture, side) => {
        if (!me) return;
        if (fixture.status !== "open") {
            toast.error("That game already kicked off");
            return;
        }
        const entry = effective.find((p) => p.fixture_id === fixture.fixture_id);
        if (!entry) {
            toast.error("Pick a side against the spread first");
            return;
        }
        const nextOu = entry.ou_pick === side ? null : side;
        setDraft(effective.map((p) => p.fixture_id === fixture.fixture_id ? { ...p, ou_pick: nextOu } : p));
    };

    const onSavePicks = async () => {
        setSaving(true);
        try {
            for (const r of dirtyRounds) {
                await api.submitMatchdayBoostPicks(tournamentId, participantId, r, subsetFor(effective, r));
            }
            await load();
            setDraft(null);
            onRefresh?.();
            toast.success("Picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Some picks failed to save — try again");
        } finally {
            setSaving(false);
        }
    };

    const onClearAll = async () => {
        const idsWithPicks = effective.filter((p) => openIds.has(p.fixture_id));
        if (idsWithPicks.length === 0) {
            toast.info("No picks to clear yet");
            return;
        }
        const roundsAffected = [...new Set(idsWithPicks.map((p) => fixturesById[p.fixture_id]?._round))];
        setClearing(true);
        try {
            for (const r of roundsAffected) {
                const remaining = effective.filter((p) => fixturesById[p.fixture_id]?._round === r && !openIds.has(p.fixture_id));
                await api.submitMatchdayBoostPicks(tournamentId, participantId, r, remaining);
            }
            await load();
            setDraft(null);
            onRefresh?.();
            toast.success("Picks cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clear picks");
        } finally {
            setClearing(false);
        }
    };

    const renderFixtureCard = (f) => {
        const pickedTeam = byFixture[f.fixture_id];
        const ouPick = ouByFixture[f.fixture_id];
        const locked = f.status !== "open";
        const isFinal = f.status === "final";
        const myPick = f.my_pick;
        // iter87.467 follow-up (testing_agent code review) — use the
        // spread-only outcome for the card/side tint, not raw combined
        // points (spread + O/U) — a covered spread + missed O/U nets
        // positive points but the TEAM pick itself was still correct;
        // conflating the two misleadingly tinted the whole card.
        const outcomeCorrect = isFinal && myPick && ["win", "win_cover"].includes(myPick.outcome);
        const outcomeWrong = isFinal && myPick && ["loss", "loss_cover"].includes(myPick.outcome);
        const outerBorderClass = outcomeCorrect ? "border-emerald-500" : outcomeWrong ? "border-red-500" : "border-white/[0.08]";
        const displayRoundLabel = (f._roundLabel || "").replace(/\s+\d+$/, "");
        const sideBtn = (side, isHome) => {
            const isPicked = pickedTeam === side.team_id;
            let rowClass = isPicked ? "border-volt bg-volt/15" : locked ? "border-white/[0.04] bg-white/[0.02] opacity-50" : "border-white/[0.08] bg-[#1A1A1A] hover:border-volt";
            if (isFinal && isPicked) rowClass = outcomeCorrect ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10";
            const lineLabel = (() => {
                if (!spreadMode) return null;
                const homeRef = (line) => isHome ? line : -line;
                if (isPicked && myPick?.captured_spread_home != null) return `Locked at ${fmtSpread(homeRef(myPick.captured_spread_home))}`;
                if (f.spread_home != null) return `Line ${fmtSpread(homeRef(f.spread_home))}`;
                if (f.spread_home_live != null) return `~${fmtSpread(homeRef(f.spread_home_live))} early`;
                return "Line TBD";
            })();
            return (
                <button
                    key={side.team_id}
                    type="button"
                    data-testid={`playoff-predictor-side-${f.fixture_id}-${side.team_id}`}
                    disabled={!me || locked}
                    onClick={() => toggle(f, side.team_id)}
                    className={`min-w-0 w-full p-2 sm:p-3 min-h-[56px] border-2 rounded-lg text-center transition-colors active:scale-[0.98] focus:outline-none focus-visible:ring-1 focus-visible:ring-white/20 ${rowClass} ${locked ? "cursor-not-allowed" : ""}`}
                >
                    <span className={`text-[13px] sm:text-sm font-bold leading-tight block break-words ${isFinal && isPicked ? (outcomeCorrect ? "text-emerald-400" : "text-red-400") : "text-white"}`}>
                        {side.name}{isPicked && <Check className="w-3 h-3 inline ml-1" />}
                    </span>
                    {lineLabel && <span className="text-[9px] uppercase tracking-wider block text-zinc-500 mt-0.5">{lineLabel}</span>}
                </button>
            );
        };
        return (
            <div
                key={f.fixture_id}
                data-testid={`playoff-predictor-fixture-${f.fixture_id}`}
                data-result={isFinal ? (outcomeCorrect ? "correct" : myPick ? "incorrect" : "no-pick") : undefined}
                className={`brutal-card bg-neutral-900 border-2 ${outerBorderClass} p-3 space-y-2`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    {locked && <Lock className="w-3 h-3 text-zinc-500 shrink-0" />}
                    <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-500">
                        {displayRoundLabel}
                    </span>
                    <span className="ml-auto text-[10px] font-mono text-zinc-500">
                        {f.played ? `FT ${f.score_a}–${f.score_b}` : f.started ? "LIVE" : `${kickoffDate(f.scheduled_at)} ${f.time_tbd ? "· Time TBD" : kickoffTime(f.scheduled_at)}`}
                    </span>
                </div>
                {/* iter87.563.1 (testing-agent caught) — the caption
                    additions landed in the dead `MatchdayBoostPanel.jsx`
                    branch first; this panel is the actual NFL/CFB
                    Playoff Predictor render target (iter87.467). */}
                {spreadMode && (
                    <SubPickLabel>
                        Spread{" "}
                        <span className="text-zinc-400 normal-case tracking-normal font-mono">
                            +{payout.win_cover ?? 6}/{fmtPts(payout.loss ?? -2)}
                        </span>
                    </SubPickLabel>
                )}
                <div className="grid items-center gap-1.5 sm:gap-2" style={{ gridTemplateColumns: "minmax(0,1fr) 24px minmax(0,1fr)" }}>
                    {sideBtn(f.home, true)}
                    <span className="text-center text-[9px] uppercase tracking-wider text-zinc-600">@</span>
                    {sideBtn(f.away, false)}
                </div>
                {spreadMode && (pickedTeam || isFinal) && (
                    <>
                        <SubPickLabel>
                            Total{" "}
                            <span className="text-zinc-400 normal-case tracking-normal font-mono">
                                +{ouPayout.correct ?? 4}/{fmtPts(ouPayout.miss ?? -2)}
                            </span>
                        </SubPickLabel>
                        <div className="grid grid-cols-2 gap-1.5 sm:gap-2" data-testid={`playoff-predictor-ou-row-${f.fixture_id}`}>
                            {["over", "under"].map((side) => {
                                const ouIsPicked = ouPick === side;
                                const ouLine = (ouIsPicked ? myPick?.captured_total_points : null) ?? f.total_points ?? f.total_points_live ?? null;
                                const label = `${side === "over" ? "Over" : "Under"} ${ouLine != null ? ouLine : "TBD"}`;
                                let ouClass = ouIsPicked ? "border-cyber bg-cyber/15 text-cyber" : locked ? "border-white/[0.04] bg-white/[0.02] opacity-50 text-zinc-600" : "border-white/[0.08] bg-[#1A1A1A] hover:border-cyber text-zinc-400";
                                if (isFinal && ouIsPicked) ouClass = myPick?.ou_outcome === "correct" ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-red-500 bg-red-500/10 text-red-400";
                                return (
                                    <button
                                        key={side}
                                        type="button"
                                        data-testid={`playoff-predictor-ou-${f.fixture_id}-${side}`}
                                        disabled={!me || locked}
                                        onClick={() => toggleOu(f, side)}
                                        className={`p-1.5 sm:p-2 min-h-[34px] border-2 rounded-lg text-center text-[11px] font-bold uppercase tracking-wide transition-colors ${ouClass} ${locked ? "cursor-not-allowed" : ""}`}
                                    >
                                        {label}{ouIsPicked && <Check className="w-3 h-3 inline ml-1" />}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
                {spreadMode && !pickedTeam && !isFinal && (
                    <p className="text-[9px] text-zinc-600 italic text-center">Pick a side above to unlock over/under</p>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4" data-testid="playoff-predictor-panel">
            <div data-testid="playoff-predictor-how-it-works" className="text-zinc-500 text-[11px] leading-relaxed flex gap-2 items-start text-left">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    {isMlsPlayoffs && <>This is the Playoff Predictor stage — 8 teams, single-knockout, Conference Semifinals through MLS Cup, win-or-go-home from here.{" "}</>}
                    Pick a side against the spread for each open game — <span className="text-volt font-bold">win + cover +{payout.win_cover ?? 6}</span> ·
                    {" "}<span className="text-cyber font-bold">win +{payout.win ?? 2}</span> ·
                    {" "}<span className="text-zinc-400 font-bold">loss that covers {payout.loss_cover ?? 0}</span> ·
                    {" "}<span className="text-heat font-bold">miss both {payout.loss ?? -2}</span>.
                    {" "}Over/Under: <span className="text-cyber font-bold">correct +{ouPayout.correct ?? 4}</span> ·
                    {" "}<span className="text-heat font-bold">miss {ouPayout.miss ?? -2}</span>.
                    {" "}Each pick locks at its game's kickoff — every round, past and future, stays visible below.
                </p>
            </div>

            {notSetYet && (
                <p data-testid="playoff-predictor-not-set-notice" className="text-xs text-zinc-500 -mt-1">
                    No games are set yet — check back once the bracket's matchups are scheduled. Sections below will fill in as rounds are scheduled.
                </p>
            )}
            <div className="space-y-4">
                <div className="space-y-3">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold" data-testid="playoff-predictor-open-heading">
                        Open — Make Your Pick ({openFixtures.length})
                    </p>
                    {openFixtures.length > 0 ? openFixtures.map(renderFixtureCard) : (
                        <p data-testid="playoff-predictor-no-open-notice" className="text-xs text-zinc-500">
                            No open games right now — check back once new matchups are set.
                        </p>
                    )}
                </div>

                <Collapsible open={showLive} onOpenChange={setShowLive}>
                        <CollapsibleTrigger
                            data-testid="playoff-predictor-live-toggle"
                            className="flex items-center justify-between w-full py-2 border-t-2 border-white/[0.08] text-left"
                        >
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                <Lock className="w-3 h-3" /> Locked & Live ({liveFixtures.length})
                            </span>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showLive ? "rotate-180" : ""}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-4 pt-3" data-testid="playoff-predictor-live-content">
                            {liveFixtures.length > 0 ? (
                                Object.entries(liveByRound).map(([roundKey, group]) => (
                                    <div key={roundKey} className="space-y-3">
                                        <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{(group.label || "").replace(/\s+\d+$/, "")}</p>
                                        {group.fixtures.map(renderFixtureCard)}
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-zinc-500" data-testid="playoff-predictor-live-empty-notice">
                                    Nothing locked or in progress right now — games move here once kickoff passes.
                                </p>
                            )}
                        </CollapsibleContent>
                    </Collapsible>

                    <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
                        <CollapsibleTrigger
                            data-testid="playoff-predictor-completed-toggle"
                            className="flex items-center justify-between w-full py-2 border-t-2 border-white/[0.08] text-left"
                        >
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                <Trophy className="w-3 h-3" /> Completed ({completedFixtures.length})
                            </span>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showCompleted ? "rotate-180" : ""}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-4 pt-3" data-testid="playoff-predictor-completed-content">
                            {completedFixtures.length > 0 ? (
                                Object.entries(completedByRound).map(([roundKey, group]) => (
                                    <div key={roundKey} className="space-y-3">
                                        <p className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold">{(group.label || "").replace(/\s+\d+$/, "")}</p>
                                        {group.fixtures.map(renderFixtureCard)}
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-zinc-500" data-testid="playoff-predictor-completed-empty-notice">
                                    No games have finished yet — check back once one wraps up.
                                </p>
                            )}
                        </CollapsibleContent>
                    </Collapsible>
                </div>

            {footerSlot && !!me && createPortal(
                <div className="glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="playoff-predictor-pick-slip">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClearAll}
                            disabled={clearing || saving || effective.filter((p) => openIds.has(p.fixture_id)).length === 0}
                            data-testid="playoff-predictor-clear-all-btn"
                            className="brutal-btn-ghost flex-1 py-2.5 text-xs font-bold uppercase tracking-widest disabled:opacity-40"
                        >
                            {clearing ? "Clearing…" : "Clear All Picks"}
                        </button>
                        <button
                            type="button"
                            onClick={onSavePicks}
                            disabled={saving || clearing || !dirty}
                            data-testid="playoff-predictor-save-btn"
                            className="brutal-btn flex-1 py-2.5 text-xs font-bold uppercase tracking-widest bg-volt text-black border-volt disabled:opacity-40"
                        >
                            {saving ? "Saving…" : "Save Picks"}
                        </button>
                    </div>
                </div>,
                footerSlot
            )}
        </div>
    );
}
